const DEFAULTS = Object.freeze({
    scope: 'serbia',
    segment: 'total',
    region: '',
    city: '',
    metric: 'yoy',
    mode: 'nominal',
    compare: false,
    benchmark: 'none',
    benchmarkMode: 'cumulative_pct',
});

const ENUMS = Object.freeze({
    scope: new Set(['serbia', 'statistical_region', 'large_city_q4_snapshot']),
    segment: new Set(['total', 'new_build', 'old_build']),
    metric: new Set(['yoy', 'qoq', 'index']),
    mode: new Set(['nominal', 'real']),
    benchmark: new Set(['none', 'spy', 'gld', 'btc']),
    benchmarkMode: new Set(['quarterly_pct', 'cumulative_pct', 'base100']),
});

const SCOPE_ALIASES = Object.freeze({
    regions: 'statistical_region',
    region: 'statistical_region',
    city: 'serbia',
    municipality: 'serbia',
    neighbourhood: 'serbia',
    neighborhood: 'serbia',
    large_city: 'large_city_q4_snapshot',
    large_city_q4: 'large_city_q4_snapshot',
    large_city_q4_snapshot: 'large_city_q4_snapshot',
});

const SEGMENT_ALIASES = Object.freeze({
    all: 'total',
    sve: 'total',
    apartments: 'new_build',
    novogradnja: 'new_build',
    houses: 'old_build',
    starogradnja: 'old_build',
});

const BENCHMARK_MODE_ALIASES = Object.freeze({
    quarterly: 'quarterly_pct',
    cumulative: 'cumulative_pct',
    base: 'base100',
    index: 'base100',
    linear: 'cumulative_pct',
    lin: 'cumulative_pct',
    logarithmic: 'cumulative_pct',
    logarithm: 'cumulative_pct',
    logaritamska: 'cumulative_pct',
    log: 'cumulative_pct',
});

const normalizeValue = (value) => {
    if (value === null || value === undefined) {
        return '';
    }

    return String(value).trim().toLowerCase();
};

const normalizeEnum = (value, key, fallback) => {
    let normalized = normalizeValue(value);

    if (key === 'scope' && SCOPE_ALIASES[normalized]) {
        normalized = SCOPE_ALIASES[normalized];
    }

    if (key === 'segment' && SEGMENT_ALIASES[normalized]) {
        normalized = SEGMENT_ALIASES[normalized];
    }

    if (!normalized) {
        return fallback;
    }

    const allowed = ENUMS[key];
    if (!allowed) {
        return normalized;
    }

    return allowed.has(normalized) ? normalized : fallback;
};

const normalizeBoolean = (value, fallback = false) => {
    const normalized = normalizeValue(value);
    if (!normalized) {
        return Boolean(fallback);
    }

    if (normalized === '1' || normalized === 'true' || normalized === 'on' || normalized === 'yes' || normalized === 'y') {
        return true;
    }

    if (normalized === '0' || normalized === 'false' || normalized === 'off' || normalized === 'no' || normalized === 'n') {
        return false;
    }

    return Boolean(fallback);
};

const normalizeBenchmarkMode = (rawMode, rawIndexScale, fallback) => {
    let normalizedMode = normalizeValue(rawMode);
    if (BENCHMARK_MODE_ALIASES[normalizedMode]) {
        normalizedMode = BENCHMARK_MODE_ALIASES[normalizedMode];
    }

    if (!ENUMS.benchmarkMode.has(normalizedMode)) {
        normalizedMode = '';
    }

    if (!normalizedMode) {
        const normalizedIndexScale = normalizeValue(rawIndexScale);
        if (BENCHMARK_MODE_ALIASES[normalizedIndexScale]) {
            normalizedMode = BENCHMARK_MODE_ALIASES[normalizedIndexScale];
        }
    }

    if (!ENUMS.benchmarkMode.has(normalizedMode)) {
        return fallback;
    }

    return normalizedMode;
};

const toState = (raw = {}, defaults = DEFAULTS) => {
    const scope = normalizeEnum(raw.scope, 'scope', defaults.scope);
    const segment = normalizeEnum(raw.segment, 'segment', defaults.segment);
    const metric = normalizeEnum(raw.metric, 'metric', defaults.metric);
    const mode = normalizeEnum(raw.mode, 'mode', defaults.mode);
    const benchmark = normalizeEnum(raw.benchmark, 'benchmark', defaults.benchmark);
    const benchmarkMode = normalizeBenchmarkMode(raw.benchmarkMode, raw.indexScale, defaults.benchmarkMode);
    const compare = normalizeBoolean(raw.compare, benchmark !== 'none' ? true : defaults.compare);

    const region = scope === 'statistical_region' ? normalizeValue(raw.region) : '';
    const city = scope === 'large_city_q4_snapshot' ? normalizeValue(raw.city) : '';

    return {
        scope,
        segment,
        region,
        city,
        metric,
        mode,
        compare,
        benchmark,
        benchmarkMode,
    };
};

const stateToApiQuery = (state = DEFAULTS) => {
    return {
        scope: state.scope,
        segment: state.segment,
        region: state.region,
        city: state.city,
    };
};

const parseFromLocation = (location, defaults = DEFAULTS) => {
    const search = new URLSearchParams(location?.search || '');
    const raw = {
        scope: search.get('scope'),
        segment: search.get('segment'),
        region: search.get('region'),
        city: search.get('city'),
        metric: search.get('metric'),
        mode: search.get('mode'),
        compare: search.get('compare'),
        benchmark: search.get('benchmark'),
        benchmarkMode: search.get('benchmarkMode'),
        indexScale: search.get('indexScale'),
    };

    return {
        state: toState(raw, defaults),
        hash: location?.hash || '',
    };
};

const toQueryString = (state, defaults = DEFAULTS, options = {}) => {
    const includeDefaults = Boolean(options.includeDefaults);
    const params = new URLSearchParams();

    Object.keys(defaults).sort().forEach((key) => {
        const value = state[key];
        if (value === '' || value === null || value === undefined) {
            return;
        }

        if (!includeDefaults && defaults[key] !== undefined && defaults[key] === value) {
            return;
        }

        params.set(key, String(value));
    });

    return params.toString();
};

const buildUrl = (state, defaults = DEFAULTS, options = {}) => {
    const pathname = options.pathname || '/market';
    const hash = options.hash || '';
    const query = toQueryString(state, defaults, options);
    return query ? `${pathname}?${query}${hash}` : `${pathname}${hash}`;
};

const applyHistory = (state, defaults = DEFAULTS, options = {}) => {
    const method = options.mode === 'replace' ? 'replaceState' : 'pushState';
    const url = buildUrl(state, defaults, options);
    window.history[method]({}, '', url);
    return url;
};

const exported = {
    DEFAULTS,
    toState,
    stateToApiQuery,
    parseFromLocation,
    toQueryString,
    buildUrl,
    applyHistory,
};

if (typeof module !== 'undefined' && module.exports) {
    module.exports = exported;
}

export {
    DEFAULTS,
    toState,
    stateToApiQuery,
    parseFromLocation,
    toQueryString,
    buildUrl,
    applyHistory,
};
